import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Section, Spinner } from "@/components/ui-parts";
import { BookOpen, Search } from "lucide-react";

export function KnowledgePage() {
  const [articles, setArticles] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/reference/knowledge").then(({ data }) => { setArticles(data); setActive(data[0]?.id); });
  }, []);
  const filtered = articles.filter((a) => !q || JSON.stringify(a).toLowerCase().includes(q.toLowerCase()));
  const article = filtered.find((a) => a.id === active) || filtered[0];
  const categories = useMemo(() => [...new Set(filtered.map((a) => a.category))], [filtered]);

  return (
    <div className="p-8 max-w-[1400px]" data-testid="knowledge-page">
      <PageHeader eyebrow="Intelligence" title="GACRF® Knowledge Center"
        description="Methodology reference, domain deep dives, and assessment guidance." />

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles..." className="pl-9" data-testid="kc-search" />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4 lg:col-span-3 card p-3 max-h-[70vh] overflow-y-auto">
          {categories.map((cat) => (
            <div key={cat} className="mb-2">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 px-2 py-1">{cat}</div>
              {filtered.filter((a) => a.category === cat).map((a) => (
                <button
                  key={a.id}
                  onClick={() => setActive(a.id)}
                  className={`w-full text-left text-xs px-2 py-1.5 rounded-sm transition-colors ${a.id === active ? "bg-[#00E5FF]/10 text-[#00E5FF] border-l-2 border-l-[#00E5FF] pl-2" : "text-slate-300 hover:bg-slate-800/40"}`}
                  data-testid={`kc-link-${a.id}`}
                >
                  {a.title}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="col-span-8 lg:col-span-9 card p-6 min-h-[60vh]">
          {article ? (
            <>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-1">{article.category}</div>
              <h1 className="font-heading text-2xl font-bold mb-4 text-slate-50" data-testid="kc-article-title">{article.title}</h1>
              <div className="md">
                {(article.content || "").trim().split(/\n\s*\n/).map((p, i) => <p key={i}>{p.split("\n").map((line, j) => <span key={j}>{line}<br /></span>)}</p>)}
              </div>
              <div className="mt-6 border-t border-slate-800/70 pt-4 text-xs text-slate-500 italic">
                <BookOpen size={12} className="inline mr-1" /> This article supports auditors, capability owners and executive stakeholders in maintaining a shared assessment vocabulary.
              </div>
            </>
          ) : <div className="text-slate-500">No article selected.</div>}
        </div>
      </div>
    </div>
  );
}

export function GlossaryPage() {
  const [terms, setTerms] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  useEffect(() => { api.get("/reference/glossary").then(({ data }) => setTerms(data)); }, []);
  const categories = useMemo(() => [...new Set(terms.map((t) => t.category))].sort(), [terms]);
  const filtered = terms.filter((t) =>
    (!cat || t.category === cat) &&
    (!q || JSON.stringify(t).toLowerCase().includes(q.toLowerCase()))
  );
  return (
    <div className="p-8 max-w-[1400px]" data-testid="glossary-page">
      <PageHeader eyebrow="Intelligence" title="GACRF Glossary & Acronyms"
        description="Reference dictionary of cybersecurity acronyms, terms and framework mappings used across GACRF." />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
        <input placeholder="Search terms..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="glossary-search" />
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="glossary-category">
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-slate-500 self-center">
          Shown: {filtered.length} / {terms.length}
        </div>
      </div>

      <Section>
        <table className="data-table">
          <thead><tr><th>Term</th><th>Full Name</th><th>Category</th><th>Description</th></tr></thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.term}>
                <td className="mono font-semibold text-slate-100">{t.term}</td>
                <td className="text-slate-300">{t.fullName}</td>
                <td><span className="badge badge-info">{t.category}</span></td>
                <td>{t.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}
